import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  Clock,
  Layers,
  PackageCheck,
  Play,
  Printer,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Loading, PageHeader, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { FinishProductionModal } from "@/components/ds/FinishProductionModal";
import { StartProductionModal } from "@/components/ds/StartProductionModal";

const STATUS = {
  PENDENTE: { label: "Pendente", cls: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  EM_PRODUCAO: { label: "Em produção", cls: "border-brand/40 bg-brand/15 text-brand" },
  CONCLUIDA: { label: "Concluída", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
  FALHOU: { label: "Falhou", cls: "border-destructive/40 bg-destructive/15 text-red-300" },
  CANCELADA: { label: "Cancelada", cls: "border-destructive/40 bg-destructive/15 text-red-300" },
};

const KIND = {
  PRODUTO: "Produto completo",
  COMPONENTE: "Peça / componente",
  MONTAGEM: "Montagem do produto",
};

const formatTime = (min) => {
  const t = Math.round(Number(min) || 0);
  if (!t) return "—";
  const h = Math.floor(t / 60);
  const m = t % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

const dataHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export default function Production() {
  const [list, setList] = useState(null);
  const [components, setComponents] = useState([]);
  const [error, setError] = useState("");
  const [finishing, setFinishing] = useState(null);
  const [starting, setStarting] = useState(null);
  const [filter, setFilter] = useState("PENDENTE");
  const [summary, setSummary] = useState({});
  const [removing, setRemoving] = useState(null);
  const [assembling, setAssembling] = useState(null);
  const [assembleBusy, setAssembleBusy] = useState(false);
  const [assemblies, setAssemblies] = useState([]);
  const [quickAssembly, setQuickAssembly] = useState(null);
  const [quickVariation, setQuickVariation] = useState("");
  const [printers, setPrinters] = useState([]);

  const load = useCallback(() => {
    setError("");
    Promise.all([
      api.get("/productions", { params: filter === "TODAS" ? {} : { status: filter } }),
      api.get("/components"),
      api.get("/components/assemblies"),
      api.get("/productions/summary"),
      api.get("/printers/availability"),
    ])
      .then(([prods, comps, asm, sum, prt]) => {
        setList(prods.data);
        setComponents(comps.data);
        setAssemblies(asm.data);
        setSummary(sum.data);
        setPrinters(prt.data?.printers || []);
      })
      .catch((e) => setError(apiError(e)));
  }, [filter]);

  useEffect(load, [load]);

  const setStatus = async (p, status) => {
    try {
      await api.put(`/productions/${p.id}/status`, { status });
      toast.success(`Produção atualizada: ${STATUS[status].label}.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setPlate = async (p, plateId) => {
    try {
      await api.put(`/productions/${p.id}/plate`, { plate_id: plateId });
      toast.success("Plate atualizada. Execuções e tempo recalculados.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reprint = async (p) => {
    try {
      const { data } = await api.post(`/productions/${p.id}/reprint`, {});
      toast.success(`Reimpressão criada: ${data.quantity_needed} un.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/productions/${p.id}`);
      toast.success("Produção excluída. O status do pedido foi recalculado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemoving(null);
    }
  };

  const assemble = async (p) => {
    setAssembleBusy(true);
    try {
      const qty = Number(p.assembly_possible || 0);
      await api.post(`/productions/${p.id}/assemble`, { quantity: qty });
      toast.success(`${qty} un montadas e adicionadas ao estoque.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAssembleBusy(false);
      setAssembling(null);
    }
  };

  const runQuickAssembly = async () => {
    const a = quickAssembly;
    if (a.variations?.length && !quickVariation) {
      toast.error("Escolha a cor do produto que será montado.");
      return;
    }
    setAssembleBusy(true);
    try {
      await api.post("/components/assemble", {
        product_id: a.product_id,
        variation_id: quickVariation || null,
        quantity: a.possible,
      });
      toast.success(`${a.possible} un montadas e adicionadas ao estoque.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAssembleBusy(false);
      setQuickAssembly(null);
      setQuickVariation("");
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (list === null) return <Loading label="Carregando a fila de produção..." />;

  const count = (s) => Number(summary[s] || 0);
  const withStock = components.filter((c) => Number(c.stock_units) > 0);

  return (
    <div className="space-y-8" data-testid="production-page">
      <PageHeader
        title="Produção"
        subtitle="Fila operacional: o que produzir, o que está rodando em cada impressora e o que já saiu. Filamento e peças só são consumidos quando você conclui a produção."
      />

      <div className="grid grid-cols-2 gap-4 stagger md:grid-cols-4">
        {[
          ["PENDENTE", Clock],
          ["EM_PRODUCAO", Printer],
          ["CONCLUIDA", CheckCircle2],
          ["FALHOU", X],
        ].map(([key, Icon]) => (
          <Surface key={key} className="p-5" data-testid={`production-stat-${key.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {STATUS[key].label}
              </p>
              <Icon className="h-4 w-4 text-brand" />
            </div>
            <p className="mt-3 font-num text-2xl font-medium">{count(key)}</p>
          </Surface>
        ))}
      </div>

      {withStock.length > 0 && (
        <Surface className="p-5" data-testid="component-stock-card">
          <div className="flex items-center gap-2">
            <Boxes className="h-4 w-4 text-brand" />
            <p className="font-display text-base font-medium">Estoque de peças</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Peças impressas ficam separadas do estoque de produto acabado e são usadas nas próximas
            montagens.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {withStock.map((c) => (
              <div
                key={c.plate_id}
                className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5"
                data-testid={`component-stock-${c.plate_id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">{c.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {c.product_name || "Sem produto"}
                  </p>
                </div>
                <span className="font-num text-sm">{c.stock_units} un</span>
              </div>
            ))}
          </div>

          {assemblies.length > 0 && (
            <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Prontas para montar
              </p>
              {assemblies.map((a) => (
                <div
                  key={a.product_id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2.5"
                  data-testid={`assembly-option-${a.product_id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm">{a.product_name}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {a.possible} un prontas para montagem ·{" "}
                      {a.components.map((c) => `${c.name} ${c.stock_units}`).join(" · ")}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setQuickVariation(a.variations?.length === 1 ? a.variations[0].id : "");
                      setQuickAssembly(a);
                    }}
                    data-testid={`quick-assemble-btn-${a.product_id}`}
                    className="h-9 shrink-0 gap-1.5 rounded-full bg-emerald-500 text-xs font-semibold text-emerald-950 hover:bg-emerald-400"
                  >
                    <PackageCheck className="h-3.5 w-3.5" /> Montar e finalizar ({a.possible})
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Surface>
      )}

      {printers.length > 0 && (
        <Surface className="p-5" data-testid="printer-queue-card">
          <div className="flex items-center gap-2">
            <Printer className="h-4 w-4 text-brand" />
            <p className="font-display text-base font-medium">Impressoras</p>
            <span className="ml-auto font-num text-xs text-muted-foreground">
              {printers.filter((p) => p.available).length} de {printers.length} livres
            </span>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {printers.map((pr) => (
              <div
                key={pr.printer_id}
                className={`rounded-lg border px-3 py-2.5 ${
                  pr.available
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-amber-500/30 bg-amber-500/5"
                }`}
                data-testid={`printer-status-${pr.printer_id}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      pr.available ? "bg-emerald-400" : "bg-amber-400"
                    }`}
                  />
                  <p className="min-w-0 truncate text-sm">{pr.name}</p>
                  <span
                    className={`ml-auto shrink-0 text-[11px] ${
                      pr.available ? "text-emerald-300" : "text-amber-300"
                    }`}
                  >
                    {pr.available ? "Livre" : "Produzindo"}
                  </span>
                </div>
                {pr.current_production && (
                  <div className="mt-1.5 border-t border-white/10 pt-1.5">
                    <p className="truncate text-[11px] text-foreground">
                      {pr.current_production.name}
                    </p>
                    <p className="font-num text-[10px] text-muted-foreground">
                      {pr.current_production.quantity} un · previsão{" "}
                      {dataHora(pr.current_production.eta)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Surface>
      )}

      <div className="flex flex-wrap gap-2" data-testid="production-filters">
        {[
          ["PENDENTE", "Pendentes"],
          ["EM_PRODUCAO", "Em produção"],
          ["CONCLUIDA", "Concluídas"],
          ["FALHOU", "Falhas"],
          ["TODAS", "Todas"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`production-filter-${key.toLowerCase()}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
              filter === key
                ? "border-brand/50 bg-brand/15 text-brand"
                : "border-white/10 text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
            {key !== "TODAS" && count(key) > 0 && (
              <span className="ml-1.5 font-num opacity-70">{count(key)}</span>
            )}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={Printer}
          title="Nenhuma produção na fila"
          description="Ajuste o filtro acima ou aguarde: quando um pedido for pago sem estoque suficiente, a produção aparece aqui automaticamente."
        />
      ) : (
        <div className="grid gap-4 stagger lg:grid-cols-2">
          {list.map((p, index) => (
            <Surface key={p.id} className="min-w-0 overflow-hidden p-5" data-testid={`production-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p
                    className="truncate font-display text-base font-medium"
                    data-testid={`production-product-${p.id}`}
                  >
                    {p.product_name}
                    {p.variation_name && (
                      <span className="ml-1.5 text-sm text-brand" data-testid={`production-variation-${p.id}`}>
                        · {p.variation_name}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {KIND[p.kind] || KIND.PRODUTO} · {dataHora(p.created_at)}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {p.purpose === "DESENVOLVIMENTO" && (
                      <span
                        className="rounded-full border border-violet-500/40 bg-violet-500/15 px-2 py-0.5 text-[10px] text-violet-300"
                        data-testid={`production-dev-badge-${p.id}`}
                      >
                        DESENVOLVIMENTO · {p.dev_project_name} · teste #{p.dev_test_attempt}
                      </span>
                    )}
                    {filter === "PENDENTE" && (
                      <span
                        className="rounded-full border border-white/15 px-2 py-0.5 font-num text-[10px] text-muted-foreground"
                        data-testid={`production-queue-position-${p.id}`}
                      >
                        #{index + 1} na fila
                      </span>
                    )}
                    <span
                      className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                      data-testid={`production-origin-${p.id}`}
                    >
                      {p.partner_name
                        ? `Reposição ${p.replenishment_number || ""} · ${p.partner_name}`
                        : p.customer_name
                        ? `Pedido ${p.order_number ? `#${p.order_number}` : ""} · ${p.customer_name}`
                        : "Estoque"}
                    </span>
                    {p.status === "PENDENTE" && p.can_finish === false && (
                      <span
                        className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                        data-testid={`production-waiting-material-${p.id}`}
                      >
                        Aguardando material
                      </span>
                    )}
                  </div>
                  {p.customization?.length > 0 && (
                    <div
                      className="mt-2 rounded-lg border border-brand/25 bg-brand/5 p-2.5"
                      data-testid={`production-customization-${p.id}`}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-wide text-brand">
                        Personalização
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {p.customization.map((c) => (
                          <li key={c.name} className="text-xs text-muted-foreground">
                            {c.name}: <span className="text-foreground">{c.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {p.plate_name && (
                    <p
                      className="mt-2 flex flex-wrap items-center gap-1.5 text-sm"
                      data-testid={`production-plate-name-${p.id}`}
                    >
                      <Layers className="h-3.5 w-3.5 text-brand" />
                      <span className="text-muted-foreground">Plate:</span>
                      <strong className="font-display uppercase tracking-wide text-brand">
                        {p.plate_name}
                      </strong>
                    </p>
                  )}
                  <p className="mt-1 text-xs" data-testid={`production-printer-${p.id}`}>
                    Impressora:{" "}
                    {p.printer_name ? (
                      <span className="text-brand">{p.printer_name}</span>
                    ) : (
                      <span className="text-muted-foreground">Não definida</span>
                    )}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS[p.status].cls}`}
                  data-testid={`production-status-${p.id}`}
                >
                  {STATUS[p.status].label}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm sm:gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.partner_name ? "Solicitado" : "Pedido"}
                  </p>
                  <p className="font-num">
                    {p.quantity_requested || p.quantity_ordered || p.quantity_needed} un
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[11px] text-muted-foreground">Em estoque</p>
                  <p className="font-num">
                    {p.stock_available_at_request ?? p.stock_available} un
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[11px] text-muted-foreground">A produzir</p>
                  <p className="font-num" data-testid={`production-needed-${p.id}`}>
                    {p.quantity_needed} un
                  </p>
                </div>
              </div>

              {p.parent_production_id && (
                <p className="mt-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid={`production-reprint-of-${p.id}`}>
                  Reimpressão de uma produção que teve falha.
                </p>
              )}

              {p.variation_compatible === false && (
                <p
                  className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-red-300"
                  data-testid={`production-incompatible-${p.id}`}
                >
                  {p.incompatible_reason}
                </p>
              )}

              {p.missing_plate && (
                <p
                  className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-red-300"
                  data-testid={`production-no-plate-${p.id}`}
                >
                  Este produto ainda não tem Plate cadastrada.
                </p>
              )}

              {p.available_plates?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Plate utilizada
                  </p>
                  {p.status === "PENDENTE" && p.available_plates.length > 1 ? (
                    <NativeSelect
                      value={p.plate_id || ""}
                      onChange={(v) => v !== p.plate_id && setPlate(p, v)}
                      data-testid={`production-plate-select-${p.id}`}
                      options={p.available_plates.map((pl) => ({
                        value: pl.id,
                        label: `${pl.name} — ${pl.output_quantity} un / ${formatTime(pl.print_time_min)}${
                          pl.id === p.plate_recommended_id ? " (recomendada)" : ""
                        }`,
                      }))}
                    />
                  ) : (
                    <p className="truncate text-sm" data-testid={`production-plate-${p.id}`}>
                      {p.plate_name} · {p.output_per_run} un por execução
                    </p>
                  )}
                  {p.plate_reasons?.length > 0 && (
                    <ul
                      className="mt-1 space-y-0.5 rounded-lg border border-white/10 bg-secondary/40 p-2.5 text-[11px] text-muted-foreground"
                      data-testid={`production-plate-reasons-${p.id}`}
                    >
                      <li className="text-foreground">
                        {p.plate_id === p.plate_recommended_id
                          ? "Plate recomendada pelo sistema"
                          : "Plate escolhida por você"}
                      </li>
                      {p.plate_reasons.map((r, i) => (
                        <li key={i}>· {r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {p.kind !== "MONTAGEM" && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div className="min-w-0">
                    <p className="truncate text-[11px] text-muted-foreground">Execuções</p>
                    <p className="font-num" data-testid={`production-runs-${p.id}`}>
                      {p.runs}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] text-muted-foreground">Vai produzir</p>
                    <p className="font-num" data-testid={`production-produced-${p.id}`}>
                      {p.quantity_produced} un
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] text-muted-foreground">Tempo total</p>
                    <p className="font-num" data-testid={`production-time-${p.id}`}>
                      {formatTime(p.total_time_min)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] text-muted-foreground">Previsão</p>
                    <p className="truncate font-num text-xs">{dataHora(p.eta)}</p>
                  </div>
                </div>
              )}

              {p.consumption_preview?.length > 0 && (
                <div className="mt-3 space-y-1.5 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid={`production-consumption-${p.id}`}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Consumo previsto de filamento
                  </p>
                  {p.consumption_preview.map((c) => (
                    <div key={c.filament_id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="min-w-0 truncate">{c.label}</span>
                      <span
                        className={`shrink-0 whitespace-nowrap font-num ${c.missing_g > 0 ? "text-red-300" : "text-muted-foreground"}`}
                      >
                        {c.needed_g} g
                        {c.missing_g > 0
                          ? ` · falta ${c.missing_g} g (disp. ${c.available_g} g)`
                          : ` · disp. ${c.available_g} g`}
                      </span>
                    </div>
                  ))}
                  {!p.can_finish && p.status !== "CONCLUIDA" && (
                    <p className="flex items-start gap-2 pt-1 text-[11px] text-red-300">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Filamento insuficiente para concluir a produção.
                    </p>
                  )}
                </div>
              )}

              {p.kind === "MONTAGEM" && (
                <div className="mt-3 space-y-1.5 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid={`production-components-${p.id}`}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Peças necessárias
                  </p>
                  {p.component_status?.map((c) => (
                    <div key={c.plate_id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="min-w-0 truncate">{c.label}</span>
                      <span className={`shrink-0 whitespace-nowrap font-num ${c.missing > 0 ? "text-red-300" : "text-emerald-300"}`}>
                        {c.needed} un · disp. {c.available} un
                        {c.missing > 0 ? ` · falta ${c.missing}` : ""}
                      </span>
                    </div>
                  ))}
                  {p.status !== "CONCLUIDA" && (
                    <p
                      className={`pt-1 text-[11px] ${p.assembly_possible > 0 ? "text-emerald-300" : "text-red-300"}`}
                      data-testid={`assembly-ready-${p.id}`}
                    >
                      {p.assembly_possible > 0
                        ? `${p.assembly_possible} un prontas para montagem`
                        : "Nenhuma unidade pronta para montagem — faltam peças."}
                    </p>
                  )}
                </div>
              )}

              <p
                className="mt-3 rounded-lg border border-brand/30 bg-brand/10 p-3 text-xs"
                data-testid={`production-destination-${p.id}`}
              >
                Destino:{" "}
                {p.quantity_for_order > 0 && (
                  <>
                    <span className="font-num">{p.quantity_for_order}</span> un para o{" "}
                    {p.partner_name ? "parceiro" : "pedido"}
                    {p.quantity_for_stock > 0 ? " · " : ""}
                  </>
                )}
                {(p.quantity_for_stock > 0 || !p.quantity_for_order) && (
                  <>
                    <span className="font-num">{p.quantity_for_stock}</span> un para o estoque
                    {p.kind === "COMPONENTE" ? " de peças" : ""}
                  </>
                )}
              </p>

              {(p.status === "PENDENTE" || p.status === "EM_PRODUCAO") && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                  {p.status === "PENDENTE" && (
                    <Button
                      size="sm"
                      onClick={() => setStarting(p)}
                      data-testid={`start-production-btn-${p.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                    >
                      <Play className="h-3.5 w-3.5" /> Iniciar
                    </Button>
                  )}
                  {p.kind === "MONTAGEM" && p.assembly_possible > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setAssembling(p)}
                      data-testid={`assemble-production-btn-${p.id}`}
                      className="h-9 gap-1.5 rounded-full bg-emerald-500 text-xs font-semibold text-emerald-950 hover:bg-emerald-400"
                    >
                      <PackageCheck className="h-3.5 w-3.5" /> Montar e finalizar (
                      {p.assembly_possible})
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setFinishing(p)}
                    data-testid={`finish-production-btn-${p.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Concluir / registrar falha
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus(p, "CANCELADA")}
                    data-testid={`cancel-production-btn-${p.id}`}
                    className="h-9 rounded-full text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!p.can_delete}
                    title={p.delete_blocked_reason || "Excluir esta produção"}
                    onClick={() => setRemoving(p)}
                    data-testid={`delete-production-btn-${p.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Excluir produção
                  </Button>
                </div>
              )}

              {(p.status === "CANCELADA" || (p.can_delete && p.status !== "PENDENTE"
                                             && p.status !== "EM_PRODUCAO")) && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!p.can_delete}
                    title={p.delete_blocked_reason || "Excluir esta produção"}
                    onClick={() => setRemoving(p)}
                    data-testid={`delete-production-btn-${p.id}`}
                    className="h-9 gap-1.5 rounded-full text-xs text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Excluir produção
                  </Button>
                </div>
              )}

              {(p.status === "CONCLUIDA" || p.status === "FALHOU") && (
                <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                  <p className="text-xs" data-testid={`production-result-${p.id}`}>
                    <span className="text-emerald-300">{p.good_quantity} boas</span>
                    {p.lost_quantity > 0 && (
                      <span className="text-red-300"> · {p.lost_quantity} refugo</span>
                    )}{" "}
                    <span className="text-muted-foreground">em {dataHora(p.finished_at)}</span>
                  </p>
                  {p.failure_reason && (
                    <p className="text-[11px] text-muted-foreground">Motivo: {p.failure_reason}</p>
                  )}
                  {p.lost_quantity > (p.reprinted_quantity || 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reprint(p)}
                      data-testid={`reprint-production-btn-${p.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Reimprimir{" "}
                      {p.lost_quantity - (p.reprinted_quantity || 0)}
                    </Button>
                  )}
                </div>
              )}

              {p.history?.length > 0 && (
                <details className="mt-3" data-testid={`production-history-${p.id}`}>
                  <summary className="cursor-pointer text-[11px] uppercase tracking-wide text-muted-foreground">
                    Histórico
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {p.history.map((h, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground">
                        {dataHora(h.at)} — {h.status}
                        {h.note ? ` · ${h.note}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Surface>
          ))}
        </div>
      )}

      {starting && (
        <StartProductionModal
          production={starting}
          onClose={() => setStarting(null)}
          onStarted={load}
        />
      )}

      {finishing && (
        <FinishProductionModal
          production={finishing}
          onClose={() => setFinishing(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-production-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta produção?</AlertDialogTitle>
            <AlertDialogDescription>
              A produção será removida e, se estiver vinculada a um pedido, o status do pedido será
              recalculado. O pedido, os produtos, o histórico e o estoque não são alterados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-production-btn">Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(removing)}
              data-testid="confirm-delete-production-btn"
            >
              Excluir produção
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!assembling} onOpenChange={(o) => !o && setAssembling(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="assemble-production-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Montar e finalizar produção</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Existem componentes suficientes para montar{" "}
                  <strong className="font-num">{assembling?.assembly_possible}</strong> un de{" "}
                  <strong>{assembling?.product_name}</strong>
                  {assembling?.variation_name ? ` (${assembling.variation_name})` : ""}.
                </p>
                <ul className="list-disc space-y-1 pl-4 text-xs">
                  <li>As peças usadas na montagem serão consumidas do estoque de peças.</li>
                  <li>
                    <span className="font-num">{assembling?.assembly_possible}</span> un do produto
                    acabado entram no estoque.
                  </li>
                  <li>
                    {assembling?.assembly_possible >= Number(assembling?.quantity_produced || 0)
                      ? "A produção será finalizada."
                      : `As ${Number(assembling?.quantity_produced || 0) - Number(assembling?.assembly_possible || 0)} un restantes continuam pendentes, aguardando peças.`}
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-assemble-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={assembleBusy}
              onClick={(e) => {
                e.preventDefault();
                assemble(assembling);
              }}
              data-testid="confirm-assemble-btn"
            >
              {assembleBusy ? "Montando..." : "Montar e finalizar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!quickAssembly} onOpenChange={(o) => !o && setQuickAssembly(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="quick-assemble-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Montar e finalizar produção</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Existem componentes suficientes para montar{" "}
                  <strong className="font-num">{quickAssembly?.possible}</strong> un de{" "}
                  <strong>{quickAssembly?.product_name}</strong>.
                </p>
                <ul className="list-disc space-y-1 pl-4 text-xs">
                  <li>As peças usadas na montagem serão consumidas do estoque de peças.</li>
                  <li>
                    <span className="font-num">{quickAssembly?.possible}</span> un do produto acabado
                    entram no estoque.
                  </li>
                  <li>A produção de montagem será registrada e finalizada.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {quickAssembly?.variations?.length > 0 && (
            <div className="mt-1">
              <p className="mb-1.5 text-xs text-muted-foreground">Cor / combinação do produto</p>
              <NativeSelect
                value={quickVariation}
                onChange={setQuickVariation}
                options={[
                  { value: "", label: "Escolha a cor..." },
                  ...quickAssembly.variations.map((v) => ({ value: v.id, label: v.name })),
                ]}
                data-testid="quick-assemble-variation-select"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-quick-assemble-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={assembleBusy}
              onClick={(e) => {
                e.preventDefault();
                runQuickAssembly();
              }}
              data-testid="confirm-quick-assemble-btn"
            >
              {assembleBusy ? "Montando..." : "Montar e finalizar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
